// BookingMetricsDashboard.jsx
//
// NEW "Booking Metrics" dashboard: ONE table (same layout as the Booking Dashboard's Stay Data table)
// with STAY DATA / BOOK DATA tabs, that adapts to the metrics chosen in the "Metrics" filter:
//   - Stay data (existing):  Room Nights, ADR, Revenue   <- /api/booking/overview (read only, unchanged)
//       shown per date (RevX, plus MKT when the MKT button is on) + totals
//   - Book data (existing):  Conversion, Property Page Visits, Search Result Views <- /api/booking/visit-conversion
//   - Rank, Rating:          like the Booking Dashboard <- /api/booking/rank-data, /api/rankly/summary
//   - Visibility Booster:     boosted commission per date (in the same date columns)
//   - Genius, Preferred, Property Page Score, Promotions, Cancellation Policy, Inclusions
//       shown as columns next to the hotel name (one value / list per hotel)
//                                                         <- /api/booking-metrics/data
// Location and Agent are filters only (not columns). Metric-specific filters (Genius status,
// promotion name, ...) appear when that metric is selected. Last sync is shown for each data source.
//
// Self-contained on purpose: it does not import from or change Dashboard.jsx's Booking Dashboard.
// Its styles are inside this file (BM_CSS below, all classes start with bm-); no separate .css file.
import React, { useState, useMemo, useEffect, useRef } from "react";
import { ChevronDown, X, Eraser, Search, RefreshCw, RotateCcw } from "lucide-react";
// "click a hotel" detail pages (copied unchanged from the old Booking Dashboard) + "Last N days";
// they live in MmtMetricsDashboard.jsx together with the old MMT detail page
import { BookingOverviewDetailView, VisitConversionDetailView, LastNDaysInput } from "./MmtMetricsDashboard.jsx";


// ---------------------------------------------------------------- styles (only for this page)
const BM_CSS = `
/* Styles for the Booking Metrics dashboard only (all classes start with bm-).
   Filters/buttons reuse the global rd-* classes from Dashboard.css, so the page looks like the
   Booking Dashboard's Stay Data table. */

.bm-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #fff;
}

/* ---------- header */
.bm-header {
  padding: 16px 24px 10px;
  background: #fff;
}
.bm-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.02em;
}
.bm-sync-line {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}
/* ---------- "Last sync" status: one pill per data source, coloured by age.
   green = synced in the last 36 h, amber = older (a daily sync was missed), grey = never.
   A pill with details (e.g. Extranet) opens a small list with each metric's own time. */
.bm-sync-pill {
  position: relative; display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; border-radius: 999px; padding: 3px 10px; border: 1px solid;
  background: #f1f5f9; border-color: #e2e8f0; color: #475569; white-space: nowrap; line-height: 1.5;
}
.bm-sync-pill.bm-sync-fresh { background: #ecfdf5; border-color: #a7f3d0; color: #047857; }
.bm-sync-pill.bm-sync-stale { background: #fffbeb; border-color: #fcd34d; color: #b45309; }
.bm-sync-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.bm-sync-label { font-weight: 800; }
.bm-sync-ago { font-weight: 500; opacity: 0.85; }
button.bm-sync-pill { cursor: pointer; font-family: inherit; }
button.bm-sync-pill:hover { filter: brightness(0.97); }
.bm-sync-pop {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 70; min-width: 330px; max-width: 90vw; box-sizing: border-box;
  background: #fff; color: #1e293b; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14); padding: 8px 10px; text-align: left; cursor: default;
  white-space: normal;
}
.bm-sync-pop-head { font-size: 11px; color: #64748b; font-weight: 500; margin-bottom: 6px; line-height: 1.4; }
.bm-sync-pop-row { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12px; border-top: 1px solid #f1f5f9; }
.bm-sync-pop-row .bm-sync-dot { width: 8px; height: 8px; }
.bm-sync-pop-name { font-weight: 700; min-width: 92px; }
.bm-sync-pop-time { font-weight: 500; color: #334155; white-space: nowrap; }
.bm-sync-pop-ago { margin-left: auto; padding-left: 10px; font-weight: 600; font-size: 11px; white-space: nowrap; }
.bm-sync-fresh-text { color: #047857; }
.bm-sync-stale-text { color: #b45309; }
.bm-sync-never-text { color: #64748b; }

/* ---------- filter bar */
.bm-filter-bar {
  border-top: 1px solid #e2e8f0;
}
.bm-filter-row {
  align-items: flex-end;
}

/* ---------- Metrics filter */
.bm-metrics-filter {
  min-width: 180px;
}
.bm-metrics-btn {
  border-color: var(--rd-header-blue, #1e2a8a) !important;
  background: #eef2ff !important;
}
.bm-metrics-btn .rd-dropdown-summary {
  font-weight: 700;
  color: var(--rd-header-blue, #1e2a8a);
}
.bm-metrics-panel {
  min-width: 240px;
  padding: 6px 0;
  max-height: none !important;   /* show all metrics without scrolling */
  overflow: visible !important;
  z-index: 50;
}
.bm-metrics-quick {
  display: flex;
  gap: 12px;
  padding: 4px 12px 6px;
  border-bottom: 1px solid #eef2f7;
}
.bm-metrics-quick button {
  border: none;
  background: none;
  color: var(--rd-header-blue, #1e2a8a);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;
}
.bm-metrics-quick button:hover {
  text-decoration: underline;
}
/* Checklist sections: a coloured header bar per group so STAY / BOOK DATA and EXTRANET read as
   separate sections, not as options. Blue = date metrics, purple = extranet. */
.bm-metrics-panel { box-sizing: border-box; padding: 6px 4px; }
.bm-metrics-section + .bm-metrics-section { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #cbd5e1; }
.bm-metrics-group {
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  margin: 2px 0 4px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}
.bm-metrics-group-perf { background: #e0e7ff; color: #1e2a8a; border-left: 4px solid #1e2a8a; }
.bm-metrics-group-ext { background: #ede9fe; color: #5b21b6; border-left: 4px solid #7c3aed; }
.bm-metrics-count { margin-left: auto; font-size: 10px; font-weight: 700; letter-spacing: 0; opacity: 0.8; }
.bm-metrics-option {
  box-sizing: border-box;
  padding-left: 22px !important;
}

/* Metric filters: ONE full-width panel. A tab per ticked extranet metric (badge = filters in use
   on that tab) and one row with the selected metric's filters, so nothing leaves gaps and the
   panel stays ~2 rows high however many metrics are ticked. */
.bm-mf-panel { margin: 10px 0 12px; border: 1px solid #e4dcfb; border-radius: 10px; background: #fcfbff; }
.bm-mf-tabs {
  display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-bottom: 1px solid #ede9fe;
  overflow-x: auto; white-space: nowrap; scrollbar-width: thin;
}
.bm-mf-heading { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #5b21b6; margin-right: 6px; flex-shrink: 0; }
.bm-mf-tab {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; border: 1px solid #ddd6fe; background: #fff;
  color: #4c1d95; border-radius: 999px; padding: 4px 12px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
}
.bm-mf-tab:hover { background: #f5f3ff; }
.bm-mf-tab-active, .bm-mf-tab-active:hover { background: #7c3aed; border-color: #7c3aed; color: #fff; }
.bm-mf-badge {
  display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 4px;
  box-sizing: border-box; border-radius: 999px; background: #7c3aed; color: #fff; font-size: 10px; font-weight: 700;
}
.bm-mf-tab-active .bm-mf-badge { background: #fff; color: #6d28d9; }
.bm-mf-clear {
  margin-left: auto; flex-shrink: 0; border: none; background: none; color: #6d28d9; font-size: 12px; font-weight: 600;
  cursor: pointer; padding: 0 0 0 12px; font-family: inherit;
}
.bm-mf-clear:hover { text-decoration: underline; }
.bm-mf-row { display: flex; flex-wrap: wrap; gap: 10px 12px; padding: 10px 12px 12px; align-items: flex-end; }
.bm-mf-row .rd-filter-group { width: 170px; }

.bm-error {
  margin: 10px 24px 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

/* ---------- the table (same look as the Booking Dashboard) */
.bm-table-card {
  flex: 1;
  min-height: 0;
  margin: 12px 24px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.bm-table-scroll {
  flex: 1;
  overflow: auto;
}
.bm-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 100%;
}
.bm-table thead th {
  position: sticky;
  z-index: 2;
  background: var(--rd-header-blue, #1e2a8a);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  white-space: nowrap;
  padding: 0 10px;
  cursor: pointer;
  user-select: none;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}
/* fixed header row heights so the 3 rows can stick on top of each other */
.bm-table thead tr.bm-h1 th { top: 0; height: 32px; }
.bm-table thead tr.bm-h2 th { top: 32px; height: 24px; }
.bm-table thead tr.bm-h3 th { top: 56px; height: 22px; }
.bm-table thead th.bm-sticky {
  left: 0;
  z-index: 4;
  text-align: left;
  min-width: 210px;
  width: 210px;
  padding-left: 14px;
}
.bm-table thead th.bm-rank-th {
  text-align: center;
  padding: 0 12px;
}
/* Hotel / Rank / Rating stay fixed when scrolling sideways (like the Booking Dashboard):
   exact border-box widths so each column starts exactly where the sticky "left" says. */
.bm-table th.bm-sticky,
.bm-table td.bm-sticky,
.bm-table td.bm-fixed-side {
  box-sizing: border-box;
}
.bm-table th.bm-sticky:not(.bm-rank-th),
.bm-table td.bm-hotel {
  width: 210px;
  min-width: 210px;
  max-width: 210px;
}
.bm-table thead th.bm-sticky.bm-rank-th,
.bm-table tbody td.bm-fixed-side {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
}
.bm-table td.bm-fixed-side {
  position: sticky;
  z-index: 2;
}
.bm-table thead th.bm-rank-th {
  z-index: 4;
}
/* edge after the fixed columns */
.bm-table td.bm-fixed-side.bm-last-fixed,
.bm-table th.bm-rank-th.bm-last-fixed {
  box-shadow: 2px 0 4px rgba(15, 23, 42, 0.08);
}
.bm-hotel-th .rd-count-badge {
  margin-right: 8px;
}
.bm-snap-th {
  min-width: 110px;
}
.bm-list-th {
  min-width: 220px;
}
.bm-date-th {
  font-size: 13px !important;
  background: #172066 !important;
}
.bm-metric-th {
  font-size: 11px !important;
  background: #24308f !important;
}
.bm-kind-th {
  font-size: 10px !important;
  font-weight: 600 !important;
  background: #2f3c9e !important;
  min-width: 64px;
}
.bm-kind-mkt {
  background: #475569 !important;
}
.bm-kind-var {
  background: #334155 !important;
}
.bm-var-up { color: #15803d !important; font-weight: 600; }
.bm-var-down { color: #b91c1c !important; font-weight: 600; }
/* clicking a hotel name opens its detail page */
.bm-table td.bm-hotel-link { cursor: pointer; color: #1e2a8a; }
.bm-table td.bm-hotel-link:hover { text-decoration: underline; }
.bm-total-head .rd-total-select { margin-left: 6px; vertical-align: middle; }
.bm-vb-th {
  background: #6d28d9 !important;
  min-width: 60px;
}
.bm-total-head {
  background: #0b1450 !important;
}

.bm-table tbody td {
  padding: 10px;
  border-bottom: 1px solid #eef2f7;
  border-right: 1px solid #e5e9f0;          /* light line between columns */
  color: #1e293b;
  vertical-align: middle;
  background: #fff;
}
.bm-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}
.bm-table tbody tr:hover td {
  background: #eef2ff;
}
.bm-table td.bm-sticky {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 1px 0 0 #e2e8f0;
}
.bm-hotel {
  font-weight: 600;
  width: 210px;
  min-width: 210px;
  max-width: 210px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bm-num {
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.bm-center {
  text-align: center;
}
.bm-mkt-td {
  color: #475569 !important;
  background: #f1f5f9 !important;
}
.bm-total-col {
  font-weight: 700;
  background: #eef2f7 !important;
}
.bm-vb-td {
  background: #faf5ff !important;
}
.bm-vb {
  font-weight: 700;
  color: #6d28d9;
}
.bm-empty {
  text-align: center;
  color: #64748b;
  padding: 28px 12px !important;
}
.bm-muted {
  color: #94a3b8;
}

/* lists (promotions, policies, inclusions) */
.bm-list-td {
  min-width: 220px;
}
.bm-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  line-height: 1.3;
}
.bm-list-item {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: space-between;
}
.bm-list-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.bm-list-more {
  font-size: 11px;
  color: #64748b;
}

/* badges and scores */
.bm-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.bm-badge-sm {
  font-size: 10px;
  padding: 1px 7px;
}
.bm-badge-green { background: #dcfce7; color: #166534; }
.bm-badge-blue { background: #dbeafe; color: #1e40af; }
.bm-badge-amber { background: #fef3c7; color: #92400e; }
.bm-badge-grey { background: #e2e8f0; color: #475569; }
.bm-badge-neutral { background: #ede9fe; color: #5b21b6; }

.bm-score {
  display: inline-block;
  min-width: 52px;
  text-align: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
}
.bm-score-top { background: #bbf7d0; color: #14532d; }
.bm-score-good { background: #dcfce7; color: #166534; }
.bm-score-mid { background: #fef9c3; color: #854d0e; }
.bm-score-low { background: #fee2e2; color: #991b1b; }

/* Desktop: fit Metrics, Hotel, Location, Agent, Search, Date range, the toggles AND the
   Refresh / Sync / Clear buttons on one row (1920px screens). On narrower laptops the date range,
   toggles and buttons wrap together to a second row instead of the buttons sitting alone. */
@media (min-width: 769px) {
  .bm-filter-row { gap: 12px; }
  .bm-filter-row > .rd-filter-group { width: 148px; }
  .bm-filter-row > .bm-metrics-filter { width: 158px; min-width: 0; }
  .bm-filter-row > .rd-search-group { width: 200px; }
  .bm-filter-row > .bm-row-tail { display: flex; align-items: flex-start; gap: 12px; flex: 1 1 auto; flex-wrap: nowrap; }
  .bm-row-tail > .rd-date-range-group { width: auto; }
  .bm-row-tail > .rd-btn-toggle { flex-shrink: 0; }
  .bm-row-tail > .rd-actions { margin-left: auto; }
  .bm-row-tail > .rd-btn-toggle { padding: 8px 12px; }
  .bm-filter-row .rd-actions { gap: 6px; flex-wrap: nowrap; }
  .bm-filter-row .rd-actions .rd-btn-sync,
  .bm-filter-row .rd-actions .rd-btn-clear { padding: 7px 10px; font-size: 13px; white-space: nowrap; }
}

@media (max-width: 768px) {
  .bm-row-tail { display: contents; }
  /* MOBILE — same pattern as the other dashboards: collapsible "Filters" panel (shared
     .occ-mobile-filter-toggle / .occ-filter-body styles), 52px gap on the left for the floating
     menu button, the page scrolls and the table keeps its own scroll area, and only the hotel
     name stays pinned when the table is swiped sideways. */
  .bm-page { overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .bm-page > * { flex-shrink: 0; } /* the page scrolls instead of squashing the header */
  .bm-header { padding: 12px 14px 8px 52px; min-height: 50px; box-sizing: border-box; }
  .bm-title { font-size: 14px; line-height: 26px; }
  .bm-tabs { gap: 6px; margin-top: 6px; }
  .bm-tab { padding: 5px 12px; font-size: 12px; }
  .bm-sync-line { gap: 6px; margin-top: 6px; }
  .bm-sync-pill { font-size: 10.5px; padding: 2px 8px; }
  .bm-legend { font-size: 10.5px; }
  .bm-error { margin-left: 12px; margin-right: 12px; font-size: 12px; }
  .bm-page .occ-filter-body:not(.occ-filter-body-collapsed) { max-height: none; overflow: visible; }
  .bm-metrics-filter { grid-column: 1 / -1; min-width: 0; width: 100%; }
  .bm-mf-panel { margin: 4px 0 10px; }
  .bm-mf-tabs { padding: 8px 10px; -webkit-mask-image: linear-gradient(to right, #000 82%, transparent); mask-image: linear-gradient(to right, #000 82%, transparent); padding-right: 28px; }
  .bm-mf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 10px; padding: 10px; }
  .bm-mf-row .rd-filter-group { width: 100%; }
  .bm-page .rd-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .bm-table-card { flex: 0 0 auto; height: 78vh; min-height: 320px; margin: 10px 12px 12px; border-radius: 6px; }
  .bm-table { font-size: 11px; }
  .bm-table tbody td { padding: 6px 8px; }
  /* only the hotel stays pinned; Rank / Rating scroll with the table */
  .bm-table td.bm-fixed-side { position: static !important; left: auto !important; box-shadow: none; }
  .bm-table thead th.bm-rank-th { left: auto !important; z-index: 3; }
  .bm-table td.bm-fixed-side.bm-last-fixed,
  .bm-table th.bm-rank-th.bm-last-fixed { box-shadow: none; }
  .bm-table th.bm-sticky:not(.bm-rank-th),
  .bm-table td.bm-hotel {
    width: 140px; min-width: 140px; max-width: 140px;
    white-space: normal; line-height: 1.25; font-size: 11px;
  }
  .bm-table td.bm-hotel { box-shadow: 2px 0 4px rgba(15, 23, 42, 0.08); }
  .bm-table thead th.bm-sticky:not(.bm-rank-th) { padding-left: 10px; }
}

/* ---------- STAY DATA / BOOK DATA tabs (like the Booking Dashboard) */
.bm-tabs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.bm-tab {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.bm-tab:hover {
  border-color: var(--rd-header-blue, #1e2a8a);
}
.bm-tab-active,
.bm-tab-active:hover {
  background: var(--rd-header-blue, #1e2a8a);
  border-color: var(--rd-header-blue, #1e2a8a);
  color: #fff;
}

/* ---------- rank / rating cells */
.bm-rank-td,
.bm-rating-td {
  min-width: 90px;
}
.bm-rank-value {
  display: block;
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
}
.bm-sub {
  display: block;
  font-size: 11px;
  color: #64748b;
}

/* ---------- chips (promotions, policies, inclusions) */
.bm-list-td {
  min-width: 260px;
  max-width: 560px;
}
.bm-chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.bm-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
  padding: 3px 4px 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
}
.bm-chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
}
.bm-chip-value {
  font-weight: 700;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
}
.bm-chip-blue { background: #e0e7ff; border-color: #c7d2fe; color: #1e3a8a; }
.bm-chip-red { background: #fee2e2; border-color: #fecaca; color: #991b1b; }
.bm-chip-amber { background: #fef3c7; border-color: #fde68a; color: #92400e; padding-right: 10px; }
.bm-chip-purple { background: #ede9fe; border-color: #ddd6fe; color: #5b21b6; }
.bm-chip-green { background: #dcfce7; border-color: #bbf7d0; color: #166534; padding-right: 10px; }
.bm-chip-grey { background: #f1f5f9; border-color: #e2e8f0; color: #334155; }
.bm-chip-slate { background: #f1f5f9; border-color: #e2e8f0; color: #1e293b; padding-right: 10px; }
.bm-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.bm-dot-green { background: #16a34a; }
.bm-dot-amber { background: #d97706; }
.bm-chip-more {
  border: 1px dashed #94a3b8;
  background: #fff;
  color: var(--rd-header-blue, #1e2a8a);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.bm-legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #64748b;
  margin-left: 6px;
}
.bm-legend-chip {
  padding: 1px 8px !important;
  font-size: 11px;
}
.bm-chip-more:hover {
  background: #eef2ff;
}
`;

// ---------------------------------------------------------------- helpers
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}
const SYNC_ALLOWED_ROLES = new Set(["SUPER ADMIN", "ADMIN", "OTA MANAGER"]);
function canUseSync() {
  try {
    return SYNC_ALLOWED_ROLES.has((localStorage.getItem("loggedInRole") || "").trim().toUpperCase());
  } catch {
    return false;
  }
}
function prettify(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^[A-Z0-9_]+$/.test(text)) {
    // ACTIVE -> Active, NOT_ENROLLED -> Not Enrolled, PREFERRED_PLUS -> Preferred Plus
    return text.toLowerCase().split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
  }
  return text;
}
function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function formatNumber(value, format) {
  const n = toNumber(value);
  if (n === null) return "—";
  if (format === "currency") return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function FilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      {onClear && (
        <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
          <Eraser size={12} />
        </button>
      )}
    </div>
  );
}
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => String(o).toLowerCase().includes(q)) : options;
  }, [options, query]);
  const allShownSelected = shown.length > 0 && shown.every((o) => selected.includes(o));
  function toggle(opt) {
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  }
  function toggleAll() {
    if (allShownSelected) onChange(selected.filter((s) => !shown.includes(s)));
    else onChange(Array.from(new Set([...selected, ...shown])));
  }
  const summary = selected.length === 0 ? "All" : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <div className="rd-filter-group" ref={ref}>
      <FilterLabel label={label} onClear={selected.length ? () => onChange([]) : null} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
          </div>
          {shown.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input type="checkbox" checked={allShownSelected} onChange={toggleAll} />
              <span>Select All{query.trim() ? ` (${shown.length} shown)` : ""}</span>
            </label>
          )}
          {shown.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            shown.map((opt) => (
              <label key={opt} className={`rd-option${selected.includes(opt) ? " rd-selected" : ""}`}>
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}
const norm = (v) => String(v || "").trim().toUpperCase();
function shortDate(iso) {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// ---------------------------------------------------------------- metrics
// kind "stay": per date, RevX/MKT.   kind "datecol": per date, one value (VB commission).
// kind "snapshot": columns next to the hotel name (one value or a list per hotel).
const METRICS = [
  // Booking Dashboard - STAY DATA tab (per date)
  { key: "room_nights", label: "Room Nights", abbr: "RN", group: "Stay data", tab: "stay", kind: "date",
    field: "room_nights_revx", marketField: "room_nights_market", format: "int", agg: "sum" },
  { key: "adr", label: "ADR", abbr: "ADR", group: "Stay data", tab: "stay", kind: "date",
    field: "adr_revx", marketField: "adr_market", format: "currency", agg: "avg" },
  { key: "revenue", label: "Revenue", abbr: "REV", group: "Stay data", tab: "stay", kind: "date",
    field: "revenue_revx", marketField: "revenue_market", format: "currency", agg: "sum" },
  // Booking Dashboard - BOOK DATA tab (per date)
  { key: "bookings", label: "Conversion (Bookings)", abbr: "CONV", group: "Book data", tab: "book", kind: "date",
    field: "daily_bookings", marketField: "daily_bookings_market", format: "int", agg: "sum" },
  { key: "page_views", label: "Property Page Visits", abbr: "VISITS", group: "Book data", tab: "book", kind: "date",
    field: "page_views", marketField: "page_views_market", format: "int", agg: "sum" },
  { key: "search_views", label: "Search Result Views", abbr: "SEARCH", group: "Book data", tab: "book", kind: "date",
    field: "search_views", marketField: "search_views_market", format: "int", agg: "sum" },
  // Extranet metrics
  { key: "genius", label: "Genius", group: "Extranet", kind: "snapshot",
    cols: [{ id: "genius_status", label: "Genius" }, { id: "genius_score", label: "Potential Score", numeric: true }] },
  { key: "preferred", label: "Preferred", group: "Extranet", kind: "snapshot",
    cols: [{ id: "preferred_level", label: "Preferred Level" }] },
  { key: "property_page_score", label: "Property Page Score", group: "Extranet", kind: "snapshot",
    cols: [{ id: "page_score", label: "Page Score", numeric: true }] },
  { key: "promotions", label: "Promotions", group: "Extranet", kind: "snapshot",
    cols: [{ id: "promotions", label: "Promotions", list: true }] },
  { key: "cancellation", label: "Cancellation Policy", group: "Extranet", kind: "snapshot",
    cols: [{ id: "cancellation", label: "Cancellation Policy", list: true }] },
  { key: "inclusions", label: "Inclusions", group: "Extranet", kind: "snapshot",
    cols: [{ id: "inclusions", label: "Inclusions", list: true }] },
  { key: "visibility_booster", label: "Visibility Booster", abbr: "VB", group: "Extranet", kind: "datecol" },
];
const TABS = [
  { id: "stay", label: "STAY DATA", dateGroup: "Stay data" },
  { id: "book", label: "BOOK DATA", dateGroup: "Book data" },
];
const METRIC_BY_KEY = Object.fromEntries(METRICS.map((m) => [m.key, m]));

// TASKS — the old "Booking Task" (Conversion / Property Page Visits / Search Result Views) has been
// REMOVED from the Task page; it is covered by the Booking Extranet Task (BOOKING_EXTRANET_TASK_SOURCE
// below). This definition is still used by Task.jsx for that task's two gates: the Book data API and
// the fields for Property Page Visits and Search Result Views (property vs market, last 15 days).
export const BOOKING_METRICS_TASK_SOURCE = {
  dashboard: "Booking Metrics",
  section: "Book data",
  endpoint: "/api/booking/visit-conversion",
  metrics: [
    { metric: "bookings", taskKey: "bookings", label: "Conversion", task: "Increase Booking.com Conversion Above Market Average" },
    { metric: "page_views", taskKey: "views", label: "Property Page Visits", task: "Increase Booking.com Visits Above Market Average" },
    { metric: "search_views", taskKey: "searchViews", label: "Search Result Views", task: null },
  ].map((m) => ({ ...m, field: METRIC_BY_KEY[m.metric].field, marketField: METRIC_BY_KEY[m.metric].marketField })),
};

// TASKS — the "Booking Extranet Task" on the Task page is generated FROM THIS DASHBOARD's extranet
// metrics, following TWO documents. Each check has a `gate`:
//   gate "visits" — "Booking.com – Visit Result Task Creation Logic": eligible only if Property Page
//                   Visits < market visits over the last 15 days.
//   gate "search" — "Booking.com – Search Result Task Creation Logic": eligible only if Search Result
//                   Views < market search results over the last 15 days.
// (The visit-result details follow.)
//   Main rule (checked in Task.jsx with the Book data sums): a property is eligible only if its
//   Property Page Visits are BELOW the market's visits over the last 15 days (equal = not eligible).
//   Then each check below decides: create a task (create: true) or not (create: false).
//   A check returns null when its input is missing -> NO task ("a missing input is not treated as
//   Yes or No"), e.g. no Genius row for the hotel, or that table failed to load.
// `key` is part of every task's id (e.g. "REVX_0054::bookext::Genius"): NEVER change it.
// m = { genius: [rows], preferred: [rows], inclusions: [rows], cancellation: [rows] } for one hotel
// (its latest snapshot); covered = the extranet sync has this hotel at all; loaded(metric) = that
// metric's table loaded without an error.
const bxText = (v) => String(v ?? "").trim().toLowerCase();
// "active" / "live" / "enabled" / "on" — but not "inactive", "not active", "non live", "deactivated"…
const bxIsActive = (v) => {
  const s = bxText(v);
  return !!s && !/^(not|non|in|un|de)[ _-]?/.test(s) && /active|live|enabled|^on$|^yes$|^true$|running/.test(s);
};
// cancellation policy that is flexible ON THE ARRIVAL DAY (free cancellation until the day of arrival)
const bxFlexibleOnArrival = (name) => {
  const s = bxText(name);
  if (!/flexib|free cancel/.test(s)) return false;
  return /arrival|check-?in day|same day|day of (arrival|check-?in)|\b0\s*days?\b|until (the )?day of/.test(s);
};
export const BOOKING_EXTRANET_TASK_SOURCE = {
  dashboard: "Booking Metrics",
  section: "Extranet",
  endpoint: "/api/booking-metrics/data",
  checks: [
    // ---- Genius and Preferred: ONE task each, combining both documents (agreed with the team).
    // gate "any": evaluated when the visits gate and/or the search gate is open; `open` says which,
    // so the task asks for exactly what is needed. Ids keep the Visit Result keys ("Genius",
    // "Preferred"), so existing tasks carry on.
    {
      // Visit Result: Genius not ACTIVE -> task.  Search Result: potential score not 100 -> task.
      gate: "any", key: "Genius", label: "Genius", task: "Activate Booking.com Genius",
      evaluate: (m, covered, loaded, open) => {
        const r = (m.genius || [])[0];
        const statusKnown = !!(r && bxText(r.status));
        const score = r ? toNumber(r.potential_score) : null;
        const needActivate = open.visits && statusKnown && !bxIsActive(r.status);
        const needScore = open.search && score !== null && score < 100;
        const state = [statusKnown ? `Genius ${prettify(r.status)}` : null, score !== null ? `Potential score ${score}` : null].filter(Boolean).join(" · ");
        if (!needActivate && !needScore) {
          const known = (open.visits && statusKnown) || (open.search && score !== null);
          return known ? { create: false, state } : null; // nothing known for the open gate(s) -> missing input
        }
        const task = needActivate && needScore
          ? "Activate Booking.com Genius and raise its potential score to 100"
          : needActivate ? "Activate Booking.com Genius" : "Raise the Booking.com Genius potential score to 100";
        return { create: true, state, task };
      },
    },
    {
      // Visit Result: not PREFERRED_PLUS -> task (target Preferred Plus).
      // Search Result: BELOW_PREFERRED -> task (target Preferred).
      gate: "any", key: "Preferred", label: "Preferred", task: "Get Booking.com Preferred Plus",
      evaluate: (m, covered, loaded, open) => {
        const r = (m.preferred || [])[0];
        const level = bxText(r?.level_by_commission);
        if (!level) return null;
        const plus = /preferred[ _-]*plus/.test(level);
        const below = /below[ _-]*preferred/.test(level);
        const state = prettify(r.level_by_commission);
        if (open.visits && !plus) {
          return { create: true, state, task: below ? "Reach Booking.com Preferred, then Preferred Plus" : "Get Booking.com Preferred Plus" };
        }
        if (open.search && below) return { create: true, state, task: "Reach the Booking.com Preferred level" };
        return { create: false, state };
      },
    },
    {
      // any inclusion live -> no task; none live -> task
      gate: "visits", key: "Inclusions", label: "Inclusions", task: "Make at least one Booking.com inclusion live",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("inclusions")) return null;
        const live = (m.inclusions || []).filter((r) => bxIsActive(r.status)).length;
        return { create: live === 0, state: live ? `${live} live` : (m.inclusions || []).length ? "No live inclusion" : "No inclusion" };
      },
    },
    {
      // cancellation policy flexible on the arrival day -> no task; otherwise (incl. no policy) task
      gate: "visits", key: "Cancellation", label: "Cancellation Policy", task: "Make the Booking.com cancellation policy flexible on the arrival day",
      evaluate: (m, covered, loaded) => {
        const rows = (m.cancellation || []).filter((r) => bxText(r.policy_name));
        if (!rows.length) {
          if (!covered || !loaded("cancellation")) return null;
          return { create: true, state: "No cancellation policy", task: "Add a flexible (arrival day) cancellation policy on Booking.com" };
        }
        const ok = rows.some((r) => bxFlexibleOnArrival(r.policy_name));
        return { create: !ok, state: ok ? "Flexible on arrival day" : String(rows[0].policy_name).trim() };
      },
    },
    // ---- Search Result tasks (gate "search": Search Result Views < market over the last 15 days).
    // Keys start with "Search" so they never share an id with the Visit Result tasks above.
    {
      // any promotion running -> no task; none -> task. The promotions table lists the promotions
      // currently on the extranet, so a row in the hotel's latest snapshot = a running promotion.
      gate: "search", key: "SearchPromotions", label: "Promotions (Search)", task: "Run at least one Booking.com promotion",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("promotions")) return null;
        const running = (m.promotions || []).filter((r) => bxText(r.promotion_name)).length;
        return { create: running === 0, state: running ? `${running} running` : "No promotion running" };
      },
    },
    {
      // page score = 100 -> no task; otherwise task
      gate: "search", key: "SearchPageScore", label: "Page Score (Search)", task: "Bring the Booking.com property page score to 100",
      evaluate: (m) => {
        const r = (m.property_page_score || [])[0];
        const score = r ? toNumber(r.score) : null;
        if (score === null) return null;
        return { create: score < 100, state: `Page score ${score}%` };
      },
    },
    {
      // Visibility Booster applied on EVERY day of the next 15 days (today + 14) -> no task;
      // otherwise task. Applied = a row for that date with a commission above 0.
      gate: "search", key: "SearchVisibilityBooster", label: "Visibility Booster (Search)", task: "Apply Booking.com Visibility Booster for the next 15 days",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("visibility_booster")) return null;
        const now = new Date();
        const p2 = (n) => String(n).padStart(2, "0");
        const days = Array.from({ length: 15 }, (_, i) => {
          const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
          return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
        });
        const applied = new Set(
          (m.visibility_booster || [])
            .filter((r) => (toNumber(r.commission) || 0) > 0)
            .map((r) => String(r.date || "").slice(0, 10))
        );
        const n = days.filter((d) => applied.has(d)).length;
        return { create: n < 15, state: n === 15 ? "Applied all 15 days" : `Applied ${n} of next 15 days` };
      },
    },
  ],
};
// fixed columns: same widths as the Booking Dashboard (property 210, rank/rating 120 each)
const BM_HOTEL_W = 210;
const BM_SIDE_W = 120;
// metrics whose data comes from /api/booking-metrics (the extranet tables)
const METRIC_TABLE_KEYS = new Set(["genius", "preferred", "property_page_score", "promotions", "cancellation", "inclusions", "visibility_booster"]);
const DEFAULT_SELECTION = {
  stay: ["room_nights", "adr", "revenue"],
  book: ["bookings", "page_views", "search_views"],
};
const STORAGE_KEY = "revx_booking_metrics_selection_v4";
const TAB_KEY = "revx_booking_metrics_tab";
// metrics offered on a tab: its own date metrics + extranet metrics
// (Rank and Rating are fixed columns, always shown, exactly like the Booking Dashboard)
const metricsForTab = (tab) => METRICS.filter((m) => !m.tab || m.tab === tab);

// ---- Rankly rating (same colours and name matching as the Booking Dashboard)
function ratingColor(rating) {
  const v = Math.round(Number(rating) * 10) / 10;
  if (!(v > 0)) return "#FFFFFF";
  if (v >= 9.1) return "#66BB6A";
  if (v >= 8.1) return "#A5D6A7";
  if (v >= 6.1) return "#FFF59D";
  return "#EF9A9A";
}
const NAME_STOPWORDS = new Set([
  "hotel", "resort", "resorts", "inn", "retreat", "retreats", "residency", "palace", "castle", "view", "house",
  "camp", "camps", "lodge", "villa", "villas", "near", "by", "the", "a", "an", "at", "of", "and", "stay", "stays",
  "homestay", "homestays", "guest", "guesthouse", "cottage", "cottages", "farm", "farms", "point", "road",
  "international", "airport", "behind", "opp", "opposite", "apartment", "apartments", "studio",
]);
function nameWords(str) {
  const words = String(str || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  const filtered = words.filter((w) => !NAME_STOPWORDS.has(w));
  return filtered.length ? filtered : words;
}
function nameScore(a, b) {
  const wa = new Set(nameWords(a));
  const wb = new Set(nameWords(b));
  if (!wa.size || !wb.size) return 0;
  let n = 0;
  wa.forEach((w) => {
    if (wb.has(w)) n += 1;
  });
  return n / Math.min(wa.size, wb.size);
}

// promotion category -> chip colour
function categoryTone(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("deep")) return "bm-chip-red";
  if (c.includes("campaign")) return "bm-chip-amber";
  if (c.includes("target")) return "bm-chip-purple";
  if (c.includes("portfolio")) return "bm-chip-blue";
  return "bm-chip-grey";
}

// Metric-specific filters (shown only while that metric is selected). Each one tests the hotel's
// rows of that metric; `value(row)` is the text used for the options and for matching.
const METRIC_FILTERS = [
  { id: "f_genius_status", metric: "genius", label: "Genius Status", value: (r) => prettify(r.status) || "No status" },
  { id: "f_preferred_level", metric: "preferred", label: "Level by Commission", value: (r) => prettify(r.level_by_commission) },
  { id: "f_page_score", metric: "property_page_score", label: "Score", value: (r) => (toNumber(r.score) === null ? "" : `${toNumber(r.score)}%`), numeric: true },
  { id: "f_promo_name", metric: "promotions", label: "Promotion Name", value: (r) => String(r.promotion_name || "").trim() },
  { id: "f_promo_category", metric: "promotions", label: "Category Name", value: (r) => String(r.category || "").trim() },
  { id: "f_policy", metric: "cancellation", label: "Policy Name", value: (r) => String(r.policy_name || "").trim() },
  { id: "f_inclusion_name", metric: "inclusions", label: "Inclusions Name", value: (r) => String(r.inclusion_name || "").trim() },
  { id: "f_inclusion_status", metric: "inclusions", label: "Inclusion Status", value: (r) => prettify(r.status) },
  { id: "f_vb_commission", metric: "visibility_booster", label: "Commission", value: (r) => (toNumber(r.commission) === null ? "" : `${toNumber(r.commission)}%`), numeric: true },
];

function badgeTone(value) {
  const v = String(value || "").toLowerCase();
  if (["active", "live", "preferred plus"].includes(v)) return "bm-badge-green";
  if (["preferred"].includes(v)) return "bm-badge-blue";
  if (["not live", "below preferred", "not enrolled", "paused"].includes(v)) return "bm-badge-amber";
  if (["expired", "inactive", "no data", "not available", "no status"].includes(v)) return "bm-badge-grey";
  return "bm-badge-neutral";
}
function scoreTone(n) {
  if (n === null) return "";
  if (n >= 100) return "bm-score-top";
  if (n >= 90) return "bm-score-good";
  if (n >= 75) return "bm-score-mid";
  return "bm-score-low";
}

// ---------------------------------------------------------------- "Last sync" pills
const SYNC_STALE_HOURS = 36; // a daily sync that is more than 36 h old was missed at least once
function parseSyncDate(value) {
  if (!value) return null;
  const text = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T00:00:00`) : new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}
function syncInfo(value) {
  const d = parseSyncDate(value);
  if (!d) return { tone: "never", when: value ? String(value) : "Not yet synced", ago: "", ms: null };
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const when = dateOnly
    ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }) })
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }), hour: "2-digit", minute: "2-digit" });
  const hours = (Date.now() - d.getTime()) / 36e5;
  const days = Math.floor(hours / 24);
  const ago = hours < 1 ? "just now" : hours < 24 ? `${Math.floor(hours)}h ago` : days === 1 ? "1 day ago" : `${days} days ago`;
  return { tone: hours > SYNC_STALE_HOURS ? "stale" : "fresh", when, ago, ms: d.getTime() };
}
// One pill per data source. `details` = [[name, value], ...]: the pill shows the OLDEST of them
// (so stale data is never hidden) and opens a list with each one's own time when clicked.
function SyncPill({ label, value, details, note }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  let shown = value;
  if (details && details.length) {
    const infos = details.map(([, v]) => syncInfo(v));
    const known = infos.filter((i) => i.ms !== null);
    shown = known.length ? details[infos.indexOf(known.reduce((a, b) => (b.ms < a.ms ? b : a)))][1] : null;
  }
  const info = syncInfo(shown);
  const expandable = details && details.length > 1;
  const neverCount = details ? details.filter(([, v]) => syncInfo(v).ms === null).length : 0;
  const partial = neverCount > 0 && neverCount < (details ? details.length : 0);
  const body = (
    <>
      <span className="bm-sync-dot" />
      <span className="bm-sync-label">{label}</span>
      <span>· {info.when}</span>
      {info.ago && <span className="bm-sync-ago">· {info.ago}</span>}
      {partial && <span className="bm-sync-ago">· {neverCount} not synced</span>}
      {expandable && <ChevronDown size={12} />}
    </>
  );
  const cls = `bm-sync-pill bm-sync-${info.tone}`;
  const title = info.tone === "stale" ? `Last sync was ${info.ago}: the daily sync may not be running.` : undefined;
  if (!expandable) return <span className={cls} title={title}>{body}</span>;
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" className={cls} title={title} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {body}
      </button>
      {open && (
        <div className="bm-sync-pop" role="dialog">
          {note && <div className="bm-sync-pop-head">{note}</div>}
          {details.map(([name, v]) => {
            const i = syncInfo(v);
            return (
              <div key={name} className={`bm-sync-pop-row bm-sync-${i.tone}-text`}>
                <span className="bm-sync-dot" />
                <span className="bm-sync-pop-name">{name}</span>
                <span className="bm-sync-pop-time">{i.when}</span>
                <span className="bm-sync-pop-ago">{i.ago}</span>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------- Metrics filter (multi)
function MetricsFilter({ tab, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const offered = metricsForTab(tab);
  const ordered = (keys) => offered.map((m) => m.key).filter((k) => keys.includes(k));
  function toggle(key) {
    if (selected.includes(key)) {
      if (selected.length > 1) onChange(selected.filter((k) => k !== key)); // at least one stays selected
    } else onChange(ordered([...selected, key]));
  }
  function setGroup(groupId, on) {
    const keys = offered.filter((m) => m.group === groupId).map((m) => m.key);
    const next = on ? ordered(Array.from(new Set([...selected, ...keys]))) : selected.filter((k) => !keys.includes(k));
    onChange(next.length ? next : DEFAULT_SELECTION[tab]);
  }
  const summary =
    selected.length === 1 ? METRIC_BY_KEY[selected[0]]?.label
      : selected.length === offered.length ? "All metrics" : `${selected.length} metrics`;
  return (
    <div className="rd-filter-group bm-metrics-filter" ref={ref}>
      <FilterLabel label="Metrics" />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn bm-metrics-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel bm-metrics-panel">
          <div className="bm-metrics-quick">
            <button onClick={() => onChange(offered.map((m) => m.key))}>Select all</button>
            <button onClick={() => onChange(DEFAULT_SELECTION[tab])}>Reset</button>
          </div>
          {[TABS.find((t) => t.id === tab).dateGroup, "Extranet"].map((g) => {
            const keys = offered.filter((m) => m.group === g).map((m) => m.key);
            const onCount = keys.filter((k) => selected.includes(k)).length;
            const allOn = onCount === keys.length;
            return (
              <div key={g} className="bm-metrics-section">
                <label className={`bm-metrics-group ${g === "Extranet" ? "bm-metrics-group-ext" : "bm-metrics-group-perf"}`}>
                  <input type="checkbox" checked={allOn} onChange={() => setGroup(g, !allOn)} />
                  <span>{g === "Extranet" ? "Extranet" : g}</span>
                  <span className="bm-metrics-count">{onCount}/{keys.length}</span>
                </label>
                {offered.filter((m) => m.group === g).map((m) => (
                  <label key={m.key} className={`rd-option bm-metrics-option${selected.includes(m.key) ? " rd-selected" : ""}`}>
                    <input type="checkbox" checked={selected.includes(m.key)} onChange={() => toggle(m.key)} />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DateRangeFilter({ dates, from, to, onFrom, onTo, onClear, onToday }) {
  const toOptions = dates.filter((d) => !from || d >= from);
  return (
    <div className="rd-filter-group rd-date-range-group">
      <FilterLabel label="Date range" onClear={onClear} />
      <div className="rd-date-inputs">
        <select value={from} onChange={(e) => onFrom(e.target.value)} className="rd-dropdown-btn">
          {dates.map((d) => <option key={d} value={d}>{shortDate(d)}</option>)}
        </select>
        <span className="rd-date-sep">to</span>
        <select value={to} onChange={(e) => onTo(e.target.value)} className="rd-dropdown-btn">
          {toOptions.map((d) => <option key={d} value={d}>{shortDate(d)}</option>)}
        </select>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today"><RotateCcw size={12} />TODAY</button>
      </div>
    </div>
  );
}

// Chips for list columns (promotions, policies, inclusions). Shows 4, "+N more" opens all.
function ChipList({ items, kind }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return <span className="bm-muted">—</span>;
  const LIMIT = 4;
  const shown = open ? items : items.slice(0, LIMIT);
  const rest = items.length - LIMIT;
  return (
    <div className="bm-chips-wrap">
      {shown.map((i, idx) => {
        if (kind === "promotions") {
          return (
            <span key={idx} className={`bm-chip ${categoryTone(i.category)}`} title={`${i.name}${i.discount !== null ? ` · ${i.discount}%` : ""}\nCategory: ${i.category || "—"}`}>
              <span className="bm-chip-text">{i.name}</span>
              {i.discount !== null && <span className="bm-chip-value">{i.discount}%</span>}
            </span>
          );
        }
        if (kind === "inclusions") {
          const live = String(i.status || "").toLowerCase() === "live";
          return (
            <span key={idx} className={`bm-chip ${live ? "bm-chip-green" : "bm-chip-amber"}`} title={`${i.name} · ${i.status || "—"}`}>
              <span className={`bm-dot ${live ? "bm-dot-green" : "bm-dot-amber"}`} />
              <span className="bm-chip-text">{i.name}</span>
            </span>
          );
        }
        return (
          <span key={idx} className="bm-chip bm-chip-slate" title={i.name}>
            <span className="bm-chip-text">{i.name}</span>
          </span>
        );
      })}
      {rest > 0 && (
        <button type="button" className="bm-chip-more" onClick={() => setOpen((o) => !o)}>
          {open ? "Show less" : `+${rest} more`}
        </button>
      )}
    </div>
  );
}

// ================================================================= page
export default function BookingMetricsDashboard() {
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem(TAB_KEY) === "book" ? "book" : "stay";
    } catch {
      return "stay";
    }
  });
  const [selectedByTab, setSelectedByTab] = useState(() => {
    const out = { ...DEFAULT_SELECTION };
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      TABS.forEach((t) => {
        const allowed = new Set(metricsForTab(t.id).map((m) => m.key));
        const valid = Array.isArray(stored[t.id]) ? stored[t.id].filter((k) => allowed.has(k)) : [];
        if (valid.length) out[t.id] = valid;
      });
    } catch {
      // ignore
    }
    return out;
  });
  const selected = selectedByTab[tab];
  const setSelected = (keys) => setSelectedByTab((prev) => ({ ...prev, [tab]: keys }));
  const [bookRows, setBookRows] = useState(null);
  const [bookError, setBookError] = useState(null);
  const [rankRows, setRankRows] = useState(null);
  const [ratingRows, setRatingRows] = useState(null);
  const [extranet, setExtranet] = useState(null);
  const [extranetError, setExtranetError] = useState(null);
  const [stayRows, setStayRows] = useState(null);
  const [stayError, setStayError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);

  const [hotelSel, setHotelSel] = useState([]);
  const [locationSel, setLocationSel] = useState([]);
  const [agentSel, setAgentSel] = useState([]);
  const [search, setSearch] = useState("");
  const [metricFilters, setMetricFilters] = useState({}); // id -> selected values
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showMkt, setShowMkt] = useState(false);
  const [showVar, setShowVar] = useState(false); // REVX − market (old Visit & Conversion VAR)
  // TOTAL column: Sum or Average per metric (old Booking Dashboard selector); default = metric's agg
  const [totalModes, setTotalModes] = useState(() =>
    Object.fromEntries(METRICS.filter((m) => m.kind === "date").map((m) => [m.key, m.agg === "avg" ? "average" : "sum"]))
  );
  const [detailKey, setDetailKey] = useState(null); // hotel whose detail page is open
  useEffect(() => {
    setDetailKey(null); // switching Stay / Book data goes back to the table
  }, [tab]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false); // phones: filters start collapsed
  const [filterTab, setFilterTab] = useState(null); // which metric's filters the "Metric filters" panel shows
  const [sort, setSort] = useState({ key: "hotel", dir: "asc" });

  const sel = useMemo(() => selected.map((k) => METRIC_BY_KEY[k]), [selected]);
  const stayMetrics = sel.filter((m) => m.kind === "date"); // date metrics of the active tab
  const snapshotMetrics = sel.filter((m) => m.kind === "snapshot");
  const showVb = selected.includes("visibility_booster");
  const hasDates = stayMetrics.length > 0 || showVb;
  const needsStay = tab === "stay" && stayMetrics.length > 0;
  const needsBook = tab === "book" && stayMetrics.length > 0;
  const needsRank = true; // Rank and Rating columns are always shown (like the Booking Dashboard)
  const needsRating = true;
  const dateRows = tab === "stay" ? stayRows : bookRows;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedByTab));
      localStorage.setItem(TAB_KEY, tab);
    } catch {
      // ignore
    }
  }, [selectedByTab, tab]);

  async function loadExtranet(force = false) {
    try {
      const res = await fetch(apiUrl(`/api/booking-metrics/data${force ? "?forceRefresh=true" : ""}`));
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setExtranet(json);
      setExtranetError(null);
      return true;
    } catch (err) {
      setExtranetError(err.message);
      if (!extranet) setExtranet({ metrics: {}, lastSync: {}, errors: {} });
      return false;
    }
  }
  async function loadStay(force = false) {
    try {
      const res = await fetch(apiUrl(`/api/booking/overview${force ? "?forceRefresh=true" : ""}`));
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setStayRows(json.data || []);
      setStayError(null);
      return true;
    } catch (err) {
      setStayError(err.message);
      setStayRows((prev) => prev || []);
      return false;
    }
  }
  async function loadBook(force = false) {
    try {
      const res = await fetch(apiUrl(`/api/booking/visit-conversion${force ? "?forceRefresh=true" : ""}`));
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setBookRows(json.data || []);
      setBookError(null);
      return true;
    } catch (err) {
      setBookError(err.message);
      setBookRows((prev) => prev || []);
      return false;
    }
  }
  async function loadRank() {
    try {
      const json = await (await fetch(apiUrl("/api/booking/rank-data"))).json();
      setRankRows(json.success ? json.data || [] : []);
    } catch {
      setRankRows([]);
    }
  }
  async function loadRating() {
    try {
      const json = await (await fetch(apiUrl("/api/rankly/summary"))).json();
      setRatingRows(json.success ? json.data || [] : []);
    } catch {
      setRatingRows([]);
    }
  }
  useEffect(() => {
    if (needsBook && bookRows === null) loadBook(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsBook]);
  useEffect(() => {
    if (needsRank && rankRows === null) loadRank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRank]);
  useEffect(() => {
    if (needsRating && ratingRows === null) loadRating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRating]);
  useEffect(() => {
    loadExtranet(false); // also carries the last-sync times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (needsStay && stayRows === null) loadStay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsStay]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  async function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    const results = await Promise.all([
      loadExtranet(true),
      needsStay ? loadStay(true) : Promise.resolve(true),
      needsBook ? loadBook(true) : Promise.resolve(true),
    ]);
    setRefreshing(false);
    setToast(results.every(Boolean)
      ? { type: "success", message: "Showing the latest data from Athena." }
      : { type: "error", message: "Couldn't refresh all data." });
  }

  // ---------------- data indexes
  const metricRows = useMemo(() => extranet?.metrics || {}, [extranet]);
  const hotels = useMemo(() => {
    const map = new Map();
    const add = (r) => {
      const key = norm(r.hotel_name);
      if (!key) return;
      if (!map.has(key)) map.set(key, { key, name: String(r.hotel_name).trim(), location: r.location || "", agent: r.agent || "" });
      const h = map.get(key);
      if (!h.location && r.location) h.location = r.location;
      if (!h.agent && r.agent) h.agent = r.agent;
    };
    if (stayMetrics.length) (dateRows || []).forEach(add);
    sel.filter((m) => METRIC_TABLE_KEYS.has(m.key)).forEach((m) => (metricRows[m.key] || []).forEach(add));
    if (map.size === 0) (rankRows || dateRows || []).forEach(add); // only rank/rating chosen
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRows, rankRows, metricRows, sel, needsRank]);

  // rank (booking_rank_data) and Rankly rating, matched like the Booking Dashboard
  const rankByHotel = useMemo(() => {
    const map = {};
    (rankRows || []).forEach((r) => {
      map[norm(r.hotel_name)] = { rank: toNumber(r.rank_in_city), total: toNumber(r.total_rank_in_city) };
    });
    return map;
  }, [rankRows]);
  const ratingLookup = useMemo(() => {
    const rows = ratingRows || [];
    const dates = Array.from(new Set(rows.map((r) => r.record_date).filter(Boolean))).sort();
    const latest = dates[dates.length - 1];
    const byName = {};
    rows.forEach((r) => {
      if (r.record_date !== latest || String(r.platform || "").toLowerCase() !== "booking" || !r.location_name) return;
      if (r.avg_rating === null || r.avg_rating === "") return;
      byName[r.location_name] = { rating: Number(r.avg_rating), reviews: Number(r.total_reviews) || 0 };
    });
    const names = Object.keys(byName);
    const lower = Object.fromEntries(names.map((n) => [n.trim().toLowerCase(), n]));
    const cache = new Map();
    return (hotelName) => {
      if (cache.has(hotelName)) return cache.get(hotelName);
      let found = byName[hotelName] ? hotelName : lower[String(hotelName).trim().toLowerCase()] || null;
      if (!found) {
        let best = 0;
        names.forEach((n) => {
          const sc = nameScore(hotelName, n);
          if (sc > best) {
            best = sc;
            found = n;
          }
        });
        if (best < 0.6) found = null;
      }
      const value = found ? byName[found] : null;
      cache.set(hotelName, value);
      return value;
    };
  }, [ratingRows]);

  const rowsByHotel = useMemo(() => {
    const out = {};
    Object.entries(metricRows).forEach(([mk, rows]) => {
      const byHotel = {};
      (rows || []).forEach((r) => {
        const key = norm(r.hotel_name);
        (byHotel[key] = byHotel[key] || []).push(r);
      });
      out[mk] = byHotel;
    });
    return out;
  }, [metricRows]);
  const stayByHotelDate = useMemo(() => {
    const map = {};
    (dateRows || []).forEach((r) => {
      const key = norm(r.hotel_name);
      (map[key] = map[key] || {})[r.record_date] = r;
    });
    return map;
  }, [dateRows]);

  // ---------------- dates (stay dates and/or VB dates)
  const allDates = useMemo(() => {
    const set = new Set();
    if (stayMetrics.length) (dateRows || []).forEach((r) => r.record_date && set.add(r.record_date));
    if (showVb) (metricRows.visibility_booster || []).forEach((r) => r.date && set.add(String(r.date).slice(0, 10)));
    return Array.from(set).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRows, metricRows, stayMetrics.length, showVb]);
  function defaultRange() {
    if (!allDates.length) return ["", ""];
    const today = new Date().toISOString().slice(0, 10);
    if (stayMetrics.length) {
      const past = allDates.filter((d) => d <= today);
      const end = past.length ? past[past.length - 1] : allDates[allDates.length - 1];
      const i = allDates.indexOf(end);
      return [allDates[Math.max(0, i - 6)], end];
    }
    const future = allDates.filter((d) => d >= today); // VB only: the next boosted dates
    const start = future.length ? future[0] : allDates[0];
    const i = allDates.indexOf(start);
    return [start, allDates[Math.min(allDates.length - 1, i + 13)]];
  }
  useEffect(() => {
    if (!allDates.length) return;
    if (!fromDate || !toDate || !allDates.includes(fromDate) || !allDates.includes(toDate)) {
      const [f, t] = defaultRange();
      setFromDate(f);
      setToDate(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDates]);
  function goToToday() {
    const today = new Date().toISOString().slice(0, 10);
    const pick = allDates.includes(today) ? today : (allDates.filter((d) => d <= today).pop() || allDates[0]);
    setFromDate(pick);
    setToDate(pick);
  }
  const dateColumns = useMemo(
    () => (hasDates ? allDates.filter((d) => d >= fromDate && d <= toDate) : []),
    [allDates, fromDate, toDate, hasDates]
  );

  // ---------------- filters
  const activeMetricFilters = METRIC_FILTERS.filter((f) => selected.includes(f.metric));
  function rowsMatching(f, hotelKey) {
    const values = metricFilters[f.id] || [];
    let rows = rowsByHotel[f.metric]?.[hotelKey] || [];
    if (f.metric === "visibility_booster") {
      rows = rows.filter((r) => {
        const d = String(r.date).slice(0, 10);
        return !hasDates || (d >= fromDate && d <= toDate);
      });
    }
    return values.length ? rows.filter((r) => values.includes(f.value(r))) : rows;
  }
  function hotelPasses(h, except) {
    if (except !== "hotel" && hotelSel.length && !hotelSel.includes(h.name)) return false;
    if (except !== "location" && locationSel.length && !locationSel.includes(h.location)) return false;
    if (except !== "agent" && agentSel.length && !agentSel.includes(h.agent)) return false;
    const q = search.trim().toLowerCase();
    if (q && ![h.name, h.location, h.agent].some((v) => String(v || "").toLowerCase().includes(q))) return false;
    for (const f of activeMetricFilters) {
      if (f.id === except) continue;
      if ((metricFilters[f.id] || []).length && rowsMatching(f, h.key).length === 0) return false;
    }
    return true;
  }
  const optionsFor = (field) =>
    Array.from(new Set(hotels.filter((h) => hotelPasses(h, field)).map((h) => (field === "hotel" ? h.name : h[field])).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  function metricFilterOptions(f) {
    const values = new Set();
    hotels.filter((h) => hotelPasses(h, f.id)).forEach((h) => {
      (rowsByHotel[f.metric]?.[h.key] || []).forEach((r) => {
        const v = f.value(r);
        if (v) values.add(v);
      });
    });
    const list = Array.from(values);
    return f.numeric ? list.sort((a, b) => parseFloat(a) - parseFloat(b)) : list.sort((a, b) => a.localeCompare(b));
  }

  // ---------------- columns (built from the selected metrics)
  const kinds = ["revx", ...(showMkt ? ["mkt"] : []), ...(showVar ? ["var"] : [])];
  const snapshotCols = snapshotMetrics.flatMap((m) => m.cols.map((c) => ({ ...c, metric: m.key })));

  // per hotel: values for every column
  function snapshotValue(h, col) {
    const f = (id) => activeMetricFilters.find((x) => x.id === id);

    if (col.id === "genius_status" || col.id === "genius_score") {
      const r = (rowsByHotel.genius?.[h.key] || [])[0];
      if (!r) return null;
      return col.id === "genius_status" ? prettify(r.status) || null : toNumber(r.potential_score);
    }
    if (col.id === "preferred_level") return prettify((rowsByHotel.preferred?.[h.key] || [])[0]?.level_by_commission) || null;
    if (col.id === "page_score") return toNumber((rowsByHotel.property_page_score?.[h.key] || [])[0]?.score);
    if (col.id === "promotions") {
      let rows = rowsByHotel.promotions?.[h.key] || [];
      [f("f_promo_name"), f("f_promo_category")].forEach((flt) => {
        if (flt && (metricFilters[flt.id] || []).length) rows = rows.filter((r) => metricFilters[flt.id].includes(flt.value(r)));
      });
      return rows
        .slice()
        .sort((a, b) => (toNumber(b.discount) || 0) - (toNumber(a.discount) || 0))
        .map((r) => ({ name: r.promotion_name || "Promotion", discount: toNumber(r.discount), category: r.category || "" }));
    }
    if (col.id === "cancellation") {
      let rows = rowsByHotel.cancellation?.[h.key] || [];
      const flt = f("f_policy");
      if (flt && (metricFilters[flt.id] || []).length) rows = rows.filter((r) => metricFilters[flt.id].includes(flt.value(r)));
      return rows.map((r) => ({ name: r.policy_name || "" }));
    }
    if (col.id === "inclusions") {
      let rows = rowsByHotel.inclusions?.[h.key] || [];
      [f("f_inclusion_name"), f("f_inclusion_status")].forEach((flt) => {
        if (flt && (metricFilters[flt.id] || []).length) rows = rows.filter((r) => metricFilters[flt.id].includes(flt.value(r)));
      });
      return rows.map((r) => ({ name: r.inclusion_name || "", status: prettify(r.status) }));
    }
    return null;
  }
  const vbByHotelDate = useMemo(() => {
    const map = {};
    (metricRows.visibility_booster || []).forEach((r) => {
      const key = norm(r.hotel_name);
      (map[key] = map[key] || {})[String(r.date).slice(0, 10)] = toNumber(r.commission);
    });
    return map;
  }, [metricRows]);
  const vbFilter = metricFilters.f_vb_commission || [];

  const tableRows = useMemo(() => {
    const visible = hotels.filter((h) => hotelPasses(h, null));
    return visible.map((h) => {
      const snap = {};
      snapshotCols.forEach((c) => {
        snap[c.id] = snapshotValue(h, c);
      });
      const stay = {}; // metric -> kind -> [values per date]
      const totals = {};
      stayMetrics.forEach((m) => {
        stay[m.key] = {};
        totals[m.key] = {};
        const mode = totalModes[m.key] || (m.agg === "avg" ? "average" : "sum");
        ["revx", "mkt"].forEach((k) => {
          const field = k === "mkt" ? m.marketField : m.field;
          const vals = dateColumns.map((d) => toNumber(stayByHotelDate[h.key]?.[d]?.[field]));
          stay[m.key][k] = vals;
          // Sum = all dates added up. Average = per date with data (an "avg" metric such as ADR
          // skips 0-days, as before).
          const present = vals.filter((v) => v !== null && (m.agg === "sum" || v > 0));
          const sum = present.reduce((s, v) => s + v, 0);
          totals[m.key][k] = present.length ? (mode === "average" ? sum / present.length : sum) : null;
        });
        // VAR = REVX − market, per date and for the total
        stay[m.key].var = stay[m.key].revx.map((v, i) => (v === null && stay[m.key].mkt[i] === null ? null : (v || 0) - (stay[m.key].mkt[i] || 0)));
        totals[m.key].var = totals[m.key].revx === null && totals[m.key].mkt === null ? null : (totals[m.key].revx || 0) - (totals[m.key].mkt || 0);
      });
      const vb = dateColumns.map((d) => {
        const v = vbByHotelDate[h.key]?.[d];
        if (v === undefined || v === null) return null;
        return vbFilter.length && !vbFilter.includes(`${v}%`) ? null : v;
      });
      return { ...h, snap, stay, totals, vb };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels, hotelSel, locationSel, agentSel, search, metricFilters, selected, dateColumns, stayByHotelDate, vbByHotelDate, rowsByHotel, rankByHotel, ratingLookup, totalModes]);

  const sortedRows = useMemo(() => {
    const arr = [...tableRows];
    const dir = sort.dir === "desc" ? -1 : 1;
    const valueOf = (r) => {
      if (sort.key === "hotel") return r.name;
      if (sort.key === "rank") return rankByHotel[r.key]?.rank ?? null;
      if (sort.key === "rating") return ratingLookup(r.name)?.rating ?? null;
      if (sort.key.startsWith("snap:")) {
        const v = r.snap[sort.key.slice(5)];
        if (v && typeof v === "object" && !Array.isArray(v)) return v.rank ?? v.rating ?? null; // rank / rating
        return Array.isArray(v) ? v.length : v;
      }
      let m = sort.key.match(/^d:(\d+):([a-z_]+):(revx|mkt|var)$/);
      if (m) return r.stay[m[2]]?.[m[3]]?.[Number(m[1])] ?? null;
      m = sort.key.match(/^vb:(\d+)$/);
      if (m) return r.vb[Number(m[1])];
      m = sort.key.match(/^t:([a-z_]+):(revx|mkt|var)$/);
      if (m) return r.totals[m[1]]?.[m[2]] ?? null;
      return null;
    };
    arr.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va === null || va === undefined || va === "") return vb === null || vb === undefined || vb === "" ? 0 : 1;
      if (vb === null || vb === undefined || vb === "") return -1;
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      return cmp * dir;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows, sort, rankByHotel, ratingLookup]);

  function headerClick(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "hotel" || key === "rank" ? "asc" : "desc" }));
  }
  const mark = (key) => (sort.key === key ? <span className="rd-sort-indicator">{sort.dir === "asc" ? "▲" : "▼"}</span> : null);

  function clearAll() {
    setHotelSel([]);
    setLocationSel([]);
    setAgentSel([]);
    setSearch("");
    setMetricFilters({});
    const [f, t] = defaultRange();
    setFromDate(f);
    setToDate(t);
  }

  // ---------------- header sizes
  const perDateCols = stayMetrics.length * kinds.length + (showVb ? 1 : 0);
  const headerRows = hasDates ? 3 : 1;
  const loading = extranet === null || (needsStay && stayRows === null) || (needsBook && bookRows === null);
  const showExtranetSync = sel.some((m) => METRIC_TABLE_KEYS.has(m.key));
  // "Metric filters" panel: one tab per ticked metric that has filters
  const mfMetrics = selected
    .filter((k) => activeMetricFilters.some((f) => f.metric === k))
    .map((k) => {
      const defs = activeMetricFilters.filter((f) => f.metric === k);
      return { key: k, label: METRIC_BY_KEY[k]?.label || k, defs, active: defs.filter((f) => (metricFilters[f.id] || []).length).length };
    });
  const mfTab = mfMetrics.find((x) => x.key === filterTab) || mfMetrics[0] || null;
  const mfActiveTotal = mfMetrics.reduce((sum, x) => sum + x.active, 0);
  const errors = [
    stayError && needsStay ? `Stay data: ${stayError}` : null,
    bookError && needsBook ? `Book data: ${bookError}` : null,
    extranetError ? `Extranet: ${extranetError}` : null,
  ]
    .concat(sel.filter((m) => extranet?.errors?.[m.key]).map((m) => `${m.label}: ${extranet.errors[m.key]}`))
    .filter(Boolean);

  return (
    <div className="bm-page">
      <style>{BM_CSS}</style>
      {toast && (
        <div className={`rd-sync-toast rd-sync-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="rd-sync-toast-close" aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      <div className="bm-header">
        <div>
          <div className="bm-title">BOOKING METRICS · {TABS.find((t) => t.id === tab).label}</div>
          <div className="bm-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`bm-tab${tab === t.id ? " bm-tab-active" : ""}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="bm-sync-line">
            {needsStay && <SyncPill label="Stay data" value={extranet?.lastSync?.stayData} />}
            {needsBook && <SyncPill label="Book data" value={extranet?.lastSync?.bookData} />}
            {showExtranetSync && <SyncPill label="Extranet" value={extranet?.lastSync?.extranet} />}
            {selected.includes("promotions") && (
              <span className="bm-legend">
                Promotion categories:
                {[["Deep deals", "bm-chip-red"], ["Campaign deals", "bm-chip-amber"], ["Portfolio deals", "bm-chip-blue"], ["Targeting", "bm-chip-purple"], ["Other", "bm-chip-grey"]].map(([label, tone]) => (
                  <span key={label} className={`bm-chip bm-legend-chip ${tone}`}>{label}</span>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>

      {detailKey ? (
        <div className="booking-subpage-body">
          {(() => {
            // the old pages are keyed by the hotel name exactly as the API returns it
            const name = (dateRows || []).find((r) => norm(r.hotel_name) === detailKey)?.hotel_name
              || hotels.find((h) => h.key === detailKey)?.name;
            return tab === "stay" ? (
              <BookingOverviewDetailView allRows={stayRows || []} initialPropertyName={name} onBack={() => setDetailKey(null)} />
            ) : (
              <VisitConversionDetailView allRows={bookRows || []} initialPropertyName={name} onBack={() => setDetailKey(null)} />
            );
          })()}
        </div>
      ) : (
      <>
      <div className="rd-filter-bar occ-filter-bar bm-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <span className="occ-mobile-filter-mini-badges">
            <span>{selected.length} metric{selected.length === 1 ? "" : "s"}</span>
            <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
          </span>
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
        <div className="rd-filter-row bm-filter-row">
          <MetricsFilter tab={tab} selected={selected} onChange={setSelected} />
          <MultiSelect label="Hotel Name" options={optionsFor("hotel")} selected={hotelSel} onChange={setHotelSel} />
          <MultiSelect label="Location" options={optionsFor("location")} selected={locationSel} onChange={setLocationSel} />
          <MultiSelect label="Agent" options={optionsFor("agent")} selected={agentSel} onChange={setAgentSel} />
          <div className="rd-filter-group rd-search-group">
            <FilterLabel label="Search" onClear={search ? () => setSearch("") : null} />
            <div className="rd-search-input-wrap">
              <Search size={14} className="rd-search-icon" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Property, location, agent..." className="rd-search-input" />
            </div>
          </div>
          {/* date range + toggles + buttons wrap together as one unit */}
          <div className="bm-row-tail">
            {hasDates && allDates.length > 0 && (
              <DateRangeFilter
                dates={allDates}
                from={fromDate}
                to={toDate}
                onFrom={(v) => {
                  setFromDate(v);
                  if (toDate && toDate < v) setToDate(v);
                }}
                onTo={setToDate}
                onClear={() => {
                  const [f, t] = defaultRange();
                  setFromDate(f);
                  setToDate(t);
                }}
                onToday={goToToday}
              />
            )}
            {hasDates && allDates.length > 0 && (
              <LastNDaysInput
                dates={allDates}
                onApply={(f, t) => {
                  setFromDate(f);
                  setToDate(t);
                }}
              />
            )}
            {stayMetrics.length > 0 && (
              <button
                type="button"
                onClick={() => setShowMkt((v) => !v)}
                className={`rd-btn-toggle${showMkt ? " rd-btn-toggle-active" : ""}`}
                title={showMkt ? "Hide market data" : "Show market data"}
              >
                MKT
              </button>
            )}
            {stayMetrics.length > 0 && (
              <button
                type="button"
                onClick={() => setShowVar((v) => !v)}
                className={`rd-btn-toggle${showVar ? " rd-btn-toggle-active" : ""}`}
                title={showVar ? "Hide variance vs market" : "Show variance vs market (REVX minus market)"}
              >
                VAR
              </button>
            )}
            <div className="rd-actions">
              {canUseSync() && (
                <button onClick={refresh} disabled={refreshing} className={`rd-btn-sync${refreshing ? " rd-btn-sync-active" : ""}`} title="Show the latest data currently in Athena">
                  <RefreshCw size={14} className={refreshing ? "rd-spin" : ""} />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              )}
              <button onClick={clearAll} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
        {mfTab && (
          <div className="bm-mf-panel">
            <div className="bm-mf-tabs" role="tablist" aria-label="Metric filters">
              <span className="bm-mf-heading">Metric filters</span>
              {mfMetrics.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  role="tab"
                  aria-selected={x.key === mfTab.key}
                  className={`bm-mf-tab${x.key === mfTab.key ? " bm-mf-tab-active" : ""}`}
                  onClick={() => setFilterTab(x.key)}
                  title={x.active ? `${x.active} filter${x.active === 1 ? "" : "s"} in use` : undefined}
                >
                  {x.label}
                  {x.active > 0 && <span className="bm-mf-badge">{x.active}</span>}
                </button>
              ))}
              {mfActiveTotal > 0 && (
                <button type="button" className="bm-mf-clear" onClick={() => setMetricFilters({})}>
                  Clear metric filters
                </button>
              )}
            </div>
            <div className="bm-mf-row" role="tabpanel">
              {mfTab.defs.map((d) => (
                <MultiSelect
                  key={d.id}
                  label={d.label}
                  options={metricFilterOptions(d)}
                  selected={metricFilters[d.id] || []}
                  onChange={(v) => setMetricFilters((prev) => ({ ...prev, [d.id]: v }))}
                />
              ))}
            </div>
          </div>
        )}
        </div>
      </div>

      {errors.length > 0 && <div className="bm-error">{errors.join(" · ")}</div>}

      {loading ? (
        <div className="rd-blank-tab">Loading booking metrics...</div>
      ) : (
        <div className="bm-table-card">
          <div className="bm-table-scroll">
            <table className={`bm-table bm-matrix${hasDates ? " bm-three-rows" : ""}`}>
              <thead>
                <tr className="bm-h1">
                  <th rowSpan={headerRows} onClick={() => headerClick("hotel")} className="bm-sticky bm-hotel-th">
                    <span className="rd-count-badge">{sortedRows.length}</span>HOTEL NAME{mark("hotel")}
                  </th>
                  <th rowSpan={headerRows} onClick={() => headerClick("rank")} className="bm-sticky bm-rank-th" style={{ left: BM_HOTEL_W }}>
                    RANK{mark("rank")}
                  </th>
                  <th rowSpan={headerRows} onClick={() => headerClick("rating")} className="bm-sticky bm-rank-th bm-last-fixed" style={{ left: BM_HOTEL_W + BM_SIDE_W }}>
                    RATING{mark("rating")}
                  </th>
                  {snapshotCols.map((c) => (
                    <th key={c.id} rowSpan={headerRows} onClick={() => headerClick(`snap:${c.id}`)} className={`bm-snap-th${c.list ? " bm-list-th" : ""}`}>
                      {c.label.toUpperCase()}{mark(`snap:${c.id}`)}
                    </th>
                  ))}
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={perDateCols} className="bm-date-th">{shortDate(d)}</th>
                  ))}
                  {dateColumns.length > 0 && stayMetrics.length > 0 && (
                    <th colSpan={stayMetrics.length * kinds.length} className="bm-date-th bm-total-head">TOTAL</th>
                  )}
                </tr>
                {hasDates && (
                  <tr className="bm-h2">
                    {dateColumns.map((d, i) => (
                      <React.Fragment key={d}>
                        {stayMetrics.map((m) => <th key={m.key} colSpan={kinds.length} className="bm-metric-th">{m.abbr}</th>)}
                        {showVb && (
                          <th rowSpan={2} onClick={() => headerClick(`vb:${i}`)} className="bm-metric-th bm-vb-th" title="Visibility Booster commission">
                            VB %{mark(`vb:${i}`)}
                          </th>
                        )}
                      </React.Fragment>
                    ))}
                    {dateColumns.length > 0 && stayMetrics.map((m) => (
                      <th key={`t-${m.key}`} colSpan={kinds.length} className="bm-metric-th bm-total-head">
                        {m.abbr}
                        <select
                          value={totalModes[m.key]}
                          onChange={(e) => setTotalModes((prev) => ({ ...prev, [m.key]: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          className="rd-total-select"
                          title="Total: sum of all dates, or average per date"
                        >
                          <option value="sum">Sum</option>
                          <option value="average">Average</option>
                        </select>
                      </th>
                    ))}
                  </tr>
                )}
                {hasDates && (
                  <tr className="bm-h3">
                    {dateColumns.map((d, i) =>
                      stayMetrics.map((m) =>
                        kinds.map((k) => (
                          <th key={`${d}-${m.key}-${k}`} onClick={() => headerClick(`d:${i}:${m.key}:${k}`)} className={`bm-kind-th${k === "mkt" ? " bm-kind-mkt" : k === "var" ? " bm-kind-var" : ""}`}>
                            {k === "mkt" ? "MKT" : k === "var" ? "VAR" : "REVX"}{mark(`d:${i}:${m.key}:${k}`)}
                          </th>
                        ))
                      )
                    )}
                    {dateColumns.length > 0 && stayMetrics.map((m) =>
                      kinds.map((k) => (
                        <th key={`t-${m.key}-${k}`} onClick={() => headerClick(`t:${m.key}:${k}`)} className={`bm-kind-th bm-total-head${k === "mkt" ? " bm-kind-mkt" : k === "var" ? " bm-kind-var" : ""}`}>
                          {k === "mkt" ? "MKT" : k === "var" ? "VAR" : "REVX"}{mark(`t:${m.key}:${k}`)}
                        </th>
                      ))
                    )}
                  </tr>
                )}
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={3 + snapshotCols.length + dateColumns.length * perDateCols + stayMetrics.length * kinds.length} className="bm-empty">
                      No hotels match these filters.
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((r, rIdx) => {
                    const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                    const rank = rankByHotel[r.key];
                    const rating = ratingLookup(r.name);
                    return (
                    <tr key={r.key}>
                      <td
                        className={`bm-sticky bm-hotel${stayMetrics.length ? " bm-hotel-link" : ""}`}
                        title={stayMetrics.length ? `Click to open ${r.name}'s detail page` : r.name}
                        onClick={stayMetrics.length ? () => setDetailKey(r.key) : undefined}
                      >
                        {r.name.toUpperCase()}
                      </td>
                      <td style={{ left: BM_HOTEL_W, width: BM_SIDE_W }} className={`rd-td-fixed rd-side rd-rank-cell bm-fixed-side ${rowBgClass}`}>
                        {rank && rank.rank !== null ? (
                          <>
                            <span className="rd-rating-value">{Number(rank.rank).toFixed(0)}</span>
                            <span className="rd-rating-reviews">Total: {rank.total !== null ? Number(rank.total).toFixed(0) : "—"}</span>
                          </>
                        ) : (
                          <span className="rd-rating-empty">—</span>
                        )}
                      </td>
                      <td
                        style={{ left: BM_HOTEL_W + BM_SIDE_W, width: BM_SIDE_W, backgroundColor: ratingColor(rating ? rating.rating : null) }}
                        className="rd-td-fixed rd-side rd-rank-cell bm-fixed-side bm-last-fixed"
                      >
                        {rating ? (
                          <>
                            <span className="rd-rating-value">{rating.rating.toFixed(1)}</span>
                            <span className="rd-rating-reviews">{rating.reviews} reviews</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      {snapshotCols.map((c) => {
                        const v = r.snap[c.id];
                        if (c.list) return <td key={c.id} className="bm-list-td"><ChipList items={v} kind={c.id} /></td>;
                        if (c.id === "page_score") {
                          return (
                            <td key={c.id} className="bm-center">
                              {v === null ? <span className="bm-muted">—</span> : <span className={`bm-score ${scoreTone(v)}`}>{v}%</span>}
                            </td>
                          );
                        }
                        if (c.numeric) return <td key={c.id} className="bm-center">{v === null ? <span className="bm-muted">—</span> : formatNumber(v)}</td>;
                        return (
                          <td key={c.id} className="bm-center">
                            {v ? <span className={`bm-badge ${badgeTone(v)}`}>{v}</span> : <span className="bm-muted">—</span>}
                          </td>
                        );
                      })}
                      {dateColumns.map((d, i) => (
                        <React.Fragment key={d}>
                          {stayMetrics.map((m) =>
                            kinds.map((k) => {
                              if (k === "var") {
                                const v = r.stay[m.key].var[i];
                                if (v === null) return <td key={`${m.key}-${k}`} className="bm-num"><span className="bm-muted">—</span></td>;
                                return (
                                  <td key={`${m.key}-${k}`} className={`bm-num ${v >= 0 ? "bm-var-up" : "bm-var-down"}`}>
                                    {v >= 0 ? "▲" : "▼"} {formatNumber(Math.abs(v), m.format)}
                                  </td>
                                );
                              }
                              return (
                                <td key={`${m.key}-${k}`} className={`bm-num${k === "mkt" ? " bm-mkt-td" : ""}`}>
                                  {formatNumber(r.stay[m.key][k][i] ?? 0, m.format)}
                                </td>
                              );
                            })
                          )}
                          {showVb && (
                            <td className="bm-num bm-vb-td">
                              {r.vb[i] === null ? <span className="bm-muted">—</span> : <span className="bm-vb">{r.vb[i]}%</span>}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                      {dateColumns.length > 0 && stayMetrics.map((m) =>
                        kinds.map((k) => {
                          if (k === "var") {
                            const v = r.totals[m.key].var;
                            if (v === null) return <td key={`t-${m.key}-${k}`} className="bm-num bm-total-col"><span className="bm-muted">—</span></td>;
                            return (
                              <td key={`t-${m.key}-${k}`} className={`bm-num bm-total-col ${v >= 0 ? "bm-var-up" : "bm-var-down"}`}>
                                {v >= 0 ? "▲" : "▼"} {formatNumber(Math.abs(v), m.format)}
                              </td>
                            );
                          }
                          return (
                            <td key={`t-${m.key}-${k}`} className={`bm-num bm-total-col${k === "mkt" ? " bm-mkt-td" : ""}`}>
                              {formatNumber(r.totals[m.key][k] ?? 0, m.format)}
                            </td>
                          );
                        })
                      )}
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
